import * as THREE from 'three';
import type * as d3 from 'd3';
import {
  highlightAttributes,
  textureProperties,
  dataComponents,
  colorConstants,
} from './constants';
import type { CrossSectionPointData } from './types';
import { PointShape } from 'src/swipe-map-3d/enums';
import { Cartesian3 } from 'cesium';
import { isNil } from 'lodash';
import { EPSGCode } from 'src/shared/enums';
import proj4 from 'proj4';

/**
 * Creates a texture for point rendering based on the specified shape.
 * The texture acts as an alpha mask - white areas are opaque, transparent areas are transparent.
 * The actual point color comes from vertex colors, not from this texture.
 * @param shapeFlag - The shape to render.
 * @returns THREE.Texture with a white shape on transparent background (used as alpha mask)
 */
export const createShapeTexture = (shapeFlag: PointShape): THREE.Texture => {
  const { canvasSize, circleRadius } = textureProperties;
  const canvas = document.createElement('canvas');
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const canvasContext = canvas.getContext('2d')!;
  canvasContext.clearRect(0, 0, canvasSize, canvasSize);

  canvasContext.fillStyle = 'rgba(255, 255, 255, 1.0)';

  if (shapeFlag === PointShape.Circle) {
    canvasContext.beginPath();
    canvasContext.arc(circleRadius, circleRadius, circleRadius, 0, 2 * Math.PI);
    canvasContext.fill();
  } else {
    const squareSize = circleRadius * 2;
    const squareX = (canvasSize - squareSize) / 2;
    const squareY = (canvasSize - squareSize) / 2;
    canvasContext.fillRect(squareX, squareY, squareSize, squareSize);
  }

  const texture = new THREE.CanvasTexture(canvas);
  // Set texture wrapping to clamp to edge to prevent black borders
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  // Use linear filtering for smoother appearance
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.premultiplyAlpha = false;
  texture.format = THREE.RGBAFormat;
  texture.needsUpdate = true;
  return texture;
};

/**
 * Creates a sprite marker for point highlighting or slope tool markers.
 * Renders as a colored dot with white border using canvas texture.
 * @param color - RGBA color string for the marker
 * @returns THREE.Sprite configured for marking points
 */
export const createMarkerSprite = (color: string): THREE.Sprite => {
  const {
    canvasSize,
    circleRadius,
    highlightOuterRadius,
    highlightInnerRadius,
  } = textureProperties;
  const canvas = document.createElement('canvas');
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvasSize, canvasSize);

  ctx.beginPath();
  ctx.arc(circleRadius, circleRadius, highlightOuterRadius, 0, 2 * Math.PI);
  ctx.fillStyle = colorConstants.white;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(circleRadius, circleRadius, highlightInnerRadius, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();

  const texture = new THREE.CanvasTexture(canvas);

  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    sizeAttenuation: true,
    transparent: true,
    depthTest: false,
  });

  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.visible = false;

  return sprite;
};

/**
 * Creates a sprite marker with a letter (A or B) for slope tool.
 * Renders as a red circle with white outline and white letter.
 * @param letter - The letter to display ('A' or 'B')
 * @returns THREE.Sprite configured for slope tool markers
 */
export const createSlopeMarkerSprite = (letter: 'A' | 'B'): THREE.Sprite => {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d', {
    willReadFrequently: false,
    alpha: true,
  })!;
  ctx.clearRect(0, 0, 128, 128);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const centerX = 64;
  const centerY = 64;
  const outerRadius = 64;
  const innerRadius = 45;

  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
  ctx.fillStyle = colorConstants.white;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
  ctx.fillStyle = colorConstants.slopeLine;
  ctx.fill();

  ctx.fillStyle = colorConstants.white;
  ctx.font = 'bold 64px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(letter, centerX, centerY);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    sizeAttenuation: true,
    transparent: true,
    depthTest: false,
  });

  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.visible = false;
  sprite.renderOrder = 1;

  return sprite;
};

/**
 * Calculates sprite scale to maintain constant pixel size.
 * @param camera Three.js orthographic camera
 * @param containerHeight Container height in pixels
 * @param desiredPixelSize Desired size in pixels (default: HIGHLIGHT_SIZE_PX)
 * @returns Scale value for sprite
 */
export const calculateSpriteScale = (
  camera: THREE.OrthographicCamera,
  containerHeight: number,
  desiredPixelSize: number = highlightAttributes.sizePx,
): number => {
  const worldHeight = camera.top - camera.bottom;
  const worldUnitsPerPixel = worldHeight / containerHeight;
  return desiredPixelSize * worldUnitsPerPixel;
};

/**
 * Calculates tube radius to maintain constant pixel width for slope line.
 * @param camera Three.js orthographic camera
 * @param containerHeight Container height in pixels
 * @param lineWidthPx Desired line width in pixels
 * @returns Tube radius in world units
 */
export const calculateTubeRadius = (
  camera: THREE.OrthographicCamera,
  containerHeight: number,
  lineWidthPx: number,
): number => {
  const worldHeight = camera.top - camera.bottom;
  const worldUnitsPerPixel = worldHeight / containerHeight;
  return (lineWidthPx * worldUnitsPerPixel) / 2;
};

/**
 * Creates a tube geometry for the slope line between two points.
 * @param pointA Start point [mileage, elevation]
 * @param pointB End point [mileage, elevation]
 * @param radius Tube radius in world units
 * @returns THREE.TubeGeometry for the line
 */
export const createSlopeLineGeometry = (
  pointA: { mileage: number; elevation: number },
  pointB: { mileage: number; elevation: number },
  radius: number,
): THREE.TubeGeometry => {
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(pointA.mileage, 0, pointA.elevation),
    new THREE.Vector3(pointB.mileage, 0, pointB.elevation),
  ]);

  return new THREE.TubeGeometry(curve, 2, radius, 8, false);
};

/**
 * Finds the closest point to given coordinates within a search radius.
 * @param data Point cloud data in TypedArray format
 * @param mileage Target mileage coordinate
 * @param elevation Target elevation coordinate
 * @param radius Maximum search radius in world units
 * @returns Object with distance and index, or null if no point found
 */
export const findClosestPoint = (
  data: CrossSectionPointData,
  mileage: number,
  elevation: number,
  radius: number,
): { distance: number; index: number } | null => {
  let closest = {
    distance: Infinity,
    index: -1,
  };

  const { sourcePositionComponents } = dataComponents;

  for (let pointIndex = 0; pointIndex < data.count; pointIndex++) {
    const positionIndex = pointIndex * sourcePositionComponents;
    const pointMileage = data.positions[positionIndex];
    const pointElevation = data.positions[positionIndex + 3];

    const mileageDifference = pointMileage - mileage;
    const elevationDifference = pointElevation - elevation;
    const distance = Math.sqrt(
      mileageDifference * mileageDifference +
        elevationDifference * elevationDifference,
    );

    if (distance < radius && distance < closest.distance) {
      closest = {
        distance: distance,
        index: pointIndex,
      };
    }
  }

  if (closest.distance < Infinity) {
    return closest;
  }
  return null;
};

/**
 * Calculates hover radius in world coordinates from pixel radius.
 * @param scaleX D3 X scale
 * @param radiusPx Radius in pixels (default: HOVER_RADIUS_PX)
 * @returns Radius in world coordinates
 */
export const calculateHoverRadius = (
  scaleX: d3.ScaleLinear<number, number>,
  radiusPx: number = highlightAttributes.hoverRadiusPx,
): number => {
  const worldPositionAtZero = scaleX.invert(0);
  const worldPositionAtRadius = scaleX.invert(radiusPx);

  return Math.abs(worldPositionAtZero - worldPositionAtRadius);
};

/**
 * Converts COPC coordinates to Cesium Cartesian3 for 3D highlighting.
 * @param copcX - X coordinate in COPC CRS
 * @param copcY - Y coordinate in COPC CRS
 * @param copcZ - Z coordinate (elevation) in COPC CRS
 * @param sourceCrs - Source CRS code
 * @returns Cesium Cartesian3 position or null if conversion fails
 */
export const convertCopcToCartesian3 = (
  copcX: number,
  copcY: number,
  copcZ: number,
  sourceCrs: number | null,
): Cartesian3 | null => {
  if (isNil(sourceCrs)) {
    return null;
  }

  try {
    const fromProj = `EPSG:${sourceCrs}`;
    const toProj = `EPSG:${EPSGCode.WGS84}`;
    const [lon, lat] = proj4(fromProj, toProj, [copcX, copcY]);

    const position = Cartesian3.fromDegrees(lon, lat, copcZ);

    return position;
  } catch (error) {
    console.error('Error converting COPC to Cartesian3:', error);
    return null;
  }
};
